import {
  DT,
  ULTI_COUNT_MUL,
  ULTI_DMG_MUL,
  ULTI_RADIUS_MUL,
  ULTI_SHOT_EVERY,
} from '../constants';
import { Ev } from '../events';
import { atan2, cos, DEG, sin, wrapAngle } from '../math';
import { Fam, PbMode } from '../pools/BulletPool';
import { SecShape, type SecondaryDef } from '../secondary';
import type { InputFrame, World } from '../World';

/* ═══════════════════════════════════════════════════════════════════════════
   L'exécution des armes secondaires (clic droit).

   Trois responsabilités, et rien d'autre : décompter la recharge, détecter le
   FRONT MONTANT du clic droit, émettre la géométrie décrite par le contenu.

   ── Pourquoi ce système est séparé de `playerSystem` ───────────────────────
   Il pourrait y tenir en trente lignes. Il n'y est pas, parce que `playerSystem`
   se termine par un `return` anticipé dans deux cas — joueur mort, mort actée
   en fin de fenêtre de death-bomb. Y greffer la recharge la ferait geler
   pendant la réapparition, silencieusement, et le joueur constaterait une arme
   « qui ne revient pas » sans jamais pouvoir relier ça à sa dernière mort. Un
   système à part rend cette décision explicite : la recharge tourne TOUJOURS,
   l'émission jamais quand le joueur n'est pas sur le terrain.

   ── Sur l'absence d'allocation ─────────────────────────────────────────────
   Rien n'est alloué ici : `volley` lit une définition partagée et écrit dans le
   pool de projectiles. Le seul état ajouté vit dans `PlayerState`, qui est un
   objet JS ordinaire créé une fois avec le monde.

   ── Une quatrième responsabilité : la CONDUITE des projectiles émis ────────
   `steerSecondaries` pilote, chaque tick, les projectiles à comportement déjà
   en vol. Elle vit ici et non dans `motionSystem` pour une raison de frontière :
   `motionSystem` intègre des vitesses, ce qu'il fait pour TOUT le monde et sans
   rien connaître des armes. Le guidage, le demi-tour d'un disque et l'ancrage
   d'un rayon sont des propriétés de l'ARME qui les a émis — ils appartiennent au
   système qui connaît les secondaires.

   ⚠️ Elle tourne AVANT l'intégration (`step` appelle secondarySystem puis
   motionSystem) et AVANT l'émission du tick : un projectile né ce tick-ci ne
   se dirige donc qu'au suivant, ce qui est correct — il n'a encore aucune
   direction acquise à corriger.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Réglages de CONDUITE ──────────────────────────────────────────────────
   Constantes de moteur, jamais de contenu, et la frontière est la même que pour
   `DIVE_APPROACH` dans `motionSystem` : ce sont les valeurs qui DÉFINISSENT la
   conduite. Les exposer au contenu permettrait d'écrire un traqueur à 90° de
   cône, c'est-à-dire une arme qui vise à la place du joueur, sans que la revue
   d'un fichier de données ne laisse deviner qu'on vient de supprimer la visée. */

/** Portée d'acquisition d'un missile guidé, en unités monde.
 *
 *  440 u, soit un peu plus du tiers de la hauteur du terrain. Le missile ne
 *  verrouille donc pas depuis le bas de l'écran : il faut l'avoir envoyé dans la
 *  bonne moitié du terrain pour qu'il trouve quelque chose. C'est ce qui laisse
 *  au joueur la responsabilité du TIR, le guidage ne rattrapant que l'erreur
 *  finale. */
const HOMING_RANGE = 440;

/** cos² du DEMI-cône d'acquisition — ici 32°.
 *
 *  ⚠️ Le cône est le garde-fou de conception le plus important du lot, et sa
 *  valeur a été MESURÉE, pas choisie. Premier réglage essayé : 55° et 520 u de
 *  portée. Le banc (`tests/sim/arsenal.test.ts`, dont la version de calibration
 *  balayait l'écart latéral de 0 à 520 u) a montré qu'un missile rattrapait
 *  alors une cible décalée de 520 u — c'est-à-dire n'importe quoi dans la moitié
 *  supérieure du terrain. La visée ne servait plus à rien : l'arme était devenue
 *  un bouton « infliger des dégâts », ce qui supprime la seule décision
 *  offensive du jeu.
 *
 *  Le couple mesuré qui tient les deux bords : à 32° et 440 u, un écart de
 *  220 u — que le tir droit manque de très loin — est rattrapé, et un écart de
 *  460 u ne l'est pas. Le test verrouille ces deux bornes ; celle du HAUT est la
 *  plus importante des deux, et c'est celle qu'on oublie d'écrire.
 *
 *  Stocké au carré pour comparer sans racine : « l'angle est sous le seuil »
 *  s'écrit dot² ≥ cos² × ‖d‖², exactement comme le bouclier directionnel. */
const HOMING_CONE_COS2 = 0.7191;

/** Portée d'un saut d'arc. Un arc ne se dirige pas, il SAUTE : il accepte donc
 *  n'importe quelle direction, y compris derrière lui, mais seulement à courte
 *  distance. C'est ce couple — aucune contrainte d'angle, forte contrainte de
 *  distance — qui en fait une arme de densité et non de visée. */
const ARC_JUMP_RANGE = 270;

/** Sentinelle « aucune contrainte d'angle » pour l'acquisition. */
const NO_CONE = -1;

/** Distance à laquelle le vaisseau rattrape un disque en retour. Généreuse : un
 *  disque qui manquerait sa reprise repartirait en orbite autour du joueur,
 *  parfaitement inoffensif et parfaitement illisible. */
const DISC_CATCH = 34;

/** Émet un projectile et rend son slot, ou -1 si le pool est saturé.
 *
 *  Le slot est rendu parce que les formes à conduite doivent équiper le
 *  projectile juste après sa naissance. Les formes balistiques l'ignorent, et
 *  c'est le bon partage : elles n'ont rien à dire de plus que « il part de là,
 *  vers là ». */
function emit(
  w: World,
  x: number,
  y: number,
  vx: number,
  vy: number,
  r: number,
  damage: number,
): number {
  const slot = w.pb.spawn(x, y, vx, vy, r, damage, Fam.PlayerSecondary);
  // Refus silencieux côté pool : c'est à l'appelant de comptabiliser. Un
  // compteur non nul ici signalerait une secondaire qui émet en boucle, ce que
  // le garde-fou de rafale est censé rendre impossible.
  if (slot < 0) w.overflow.playerBullets++;
  return slot;
}

/** Le canon du vaisseau est en avant du centre : émettre depuis le centre ferait
 *  naître les projectiles DANS le mesh, et à 2 000 u/s le premier tiers du vol
 *  serait invisible. Même valeur que le tir primaire, pour que les deux armes
 *  aient visiblement la même bouche. */
const MUZZLE_Y = 30;

/**
 * Ennemi le plus proche visible depuis (bx, by), dans la direction (hx, hy).
 *
 * Rend un SLOT, ou -1. Les critères d'exclusion sont exactement ceux de la
 * boucle de collision — mort en attente de nettoyage, télégraphe en cours,
 * noyau blindé par ses parties — et ils doivent le rester : un missile qui
 * verrouille un ennemi intouchable tourne autour de lui jusqu'à sortir du
 * terrain, ce que le joueur lit comme une arme cassée.
 *
 * `cone2` vaut `NO_CONE` pour accepter n'importe quelle direction (un arc
 * saute en arrière), ou le cos² du demi-angle toléré.
 *
 * Zéro allocation : on rend un entier, jamais une structure. La boucle est un
 * balayage linéaire sur un pool dense — la même force brute assumée que la
 * collision, et sur bien moins de paires.
 */
function acquire(
  w: World,
  bx: number,
  by: number,
  hx: number,
  hy: number,
  range: number,
  cone2: number,
  excludeUid: number,
): number {
  const en = w.enemies;
  let best = -1;
  let bestD2 = range * range;

  for (let j = en.count - 1; j >= 0; j--) {
    const e = en.at(j);
    if (en.hp[e]! <= 0) continue;
    if (en.telegraph[e]! > 0) continue;
    if (en.shielded[e] === 1 && en.livingParts(e) > 0) continue;
    // Un arc ne revient pas sur ses pas : sans cette exclusion il ferait
    // l'aller-retour entre deux ennemis voisins et « sauter » ne voudrait plus
    // rien dire.
    if (excludeUid !== 0 && en.uid[e] === excludeUid) continue;

    const dx = en.x[e]! - bx;
    const dy = en.y[e]! - by;
    const d2 = dx * dx + dy * dy;
    if (d2 >= bestD2) continue;

    if (cone2 !== NO_CONE) {
      const dot = dx * hx + dy * hy;
      // Derrière : rejeté avant l'élévation au carré, qui perdrait le signe.
      if (dot <= 0) continue;
      if (dot * dot < cone2 * d2) continue;
    }

    best = e;
    bestD2 = d2;
  }
  return best;
}

/**
 * Fait tourner la vitesse d'un projectile vers (tx, ty), d'au plus `rate`
 * radians ce tick. Conserve exactement sa NORME.
 *
 * Recalculer la norme puis la réappliquer, plutôt que d'ajouter un vecteur de
 * correction : une correction additive accélère le projectile à chaque virage,
 * et un missile qui poursuit longtemps finirait par dépasser le plafond de
 * vitesse du jeu sans qu'aucune donnée n'ait changé.
 */
function steerToward(
  pb: World['pb'],
  s: number,
  tx: number,
  ty: number,
  rate: number,
): void {
  const vx = pb.vx[s]!;
  const vy = pb.vy[s]!;
  const sp = Math.sqrt(vx * vx + vy * vy);
  if (sp < 1e-6) return;

  const cur = atan2(vy, vx);
  const want = atan2(ty - pb.y[s]!, tx - pb.x[s]!);
  let d = wrapAngle(want - cur);
  const max = rate * DT;
  if (d > max) d = max;
  else if (d < -max) d = -max;

  const a = cur + d;
  pb.vx[s] = cos(a) * sp;
  pb.vy[s] = sin(a) * sp;
}

/**
 * La CONDUITE des projectiles à comportement, un tick.
 *
 * ⚠️ Itération EN ARRIÈRE : cette boucle libère (disque rattrapé, rayon
 * expiré), et `free` remonte le dernier slot vivant à la place libérée — une
 * boucle en avant sauterait ce slot-là pendant une frame. Le piège est
 * documenté dans `SoAPool`.
 */
export function steerSecondaries(w: World): void {
  const pb = w.pb;
  const p = w.player;

  for (let i = pb.count - 1; i >= 0; i--) {
    const s = pb.at(i);
    const mode = pb.mode[s]!;
    if (mode === PbMode.Ballistic) continue;

    if (mode === PbMode.Beam) {
      /* Le rayon ne vole pas : il est REPOSITIONNÉ sur le vaisseau à chaque
         tick, et c'est toute la différence avec le Faisceau bref. Sa vitesse
         reste nulle, donc `motionSystem` l'intègre sans le déplacer.

         Le balayage est correct par construction : `savePrev` a mémorisé la
         position d'avant-tick en tête de `step`, donc le segment balayé couvre
         tout le trajet parcouru par le rayon pendant que le joueur se déplaçait.
         Un rayon qui traverse latéralement une file d'ennemis les touche tous,
         sans qu'aucune ligne de collision n'ait été écrite pour lui.

         ⚠️ La mort du joueur ÉTEINT le rayon, à la différence des disques et des
         missiles qui poursuivent leur course. La règle est la même que celle qui
         interrompt une rafale en cours plus bas dans ce fichier, et pour la même
         raison : ce qui a quitté le vaisseau lui survit, ce qui y est encore
         accroché tirerait depuis une épave.

         ⚠️ Conséquence connue et acceptée : un rayon allumé pendant que le
         vaisseau est très haut voit ses segments de tête sortir du cadre, où
         `motionSystem` les élimine comme n'importe quel projectile. Le rayon
         reste alors court jusqu'à son extinction, même si le joueur redescend.
         C'est visuellement correct — la partie perdue était hors de l'écran — et
         le corriger demanderait de soustraire ces segments à la règle
         d'élimination, c'est-à-dire d'ouvrir la seule porte par laquelle un pool
         fuit. */
      const life = pb.life[s]! - DT;
      if (life <= 0 || p.dead) {
        pb.free(s);
        continue;
      }
      pb.life[s] = life;
      pb.x[s] = p.x;
      pb.y[s] = p.y + MUZZLE_Y + pb.param[s]!;
      continue;
    }

    const rate = pb.param[s]!;

    if (mode === PbMode.Pierce) {
      /* Deux temps, et le second est ce qui distingue la Scie de tout le reste
         du catalogue : le disque monte pendant `life`, puis rentre. Le retour
         est un guidage vers le VAISSEAU — donc vers un point que le joueur
         déplace lui-même. C'est le seul endroit du jeu où bouger après avoir
         tiré change ce que le tir rapporte. */
      const life = pb.life[s]!;
      if (life > 0) {
        pb.life[s] = life - DT;
        continue;
      }
      steerToward(pb, s, p.x, p.y, rate);
      const dx = pb.x[s]! - p.x;
      const dy = pb.y[s]! - p.y;
      if (dx * dx + dy * dy < DISC_CATCH * DISC_CATCH) pb.free(s);
      continue;
    }

    /* Guidage et saut partagent la même mécanique — acquérir, tourner — et ne
       diffèrent que par leurs bornes. C'est délibéré : ce sont deux réglages
       d'une seule capacité, pas deux capacités. Un traqueur voit loin et tourne
       lentement ; un arc ne voit qu'à bout portant mais se retourne d'un coup. */
    const vx = pb.vx[s]!;
    const vy = pb.vy[s]!;
    const sp = Math.sqrt(vx * vx + vy * vy);
    if (sp < 1e-6) continue;

    const isArc = mode === PbMode.Arc;
    const target = acquire(
      w,
      pb.x[s]!,
      pb.y[s]!,
      vx / sp,
      vy / sp,
      isArc ? ARC_JUMP_RANGE : HOMING_RANGE,
      isArc ? NO_CONE : HOMING_CONE_COS2,
      isArc ? pb.lastUid[s]! : 0,
    );
    // Rien devant : le projectile poursuit sa course. Un missile qui perd sa
    // cible ne doit pas s'arrêter de voler, ni tourner dans le vide.
    if (target < 0) continue;

    steerToward(pb, s, w.enemies.x[target]!, w.enemies.y[target]!, rate);
  }
}

/**
 * UNE volée. Appelée une fois pour une arme ponctuelle, `shots` fois pour une
 * rafale — et toujours depuis la position COURANTE du vaisseau, jamais depuis
 * celle mémorisée au déclenchement : c'est ce qui permet de balayer avec le
 * faisceau, et donc ce qui en fait une arme de placement.
 */
function volley(w: World, def: SecondaryDef): void {
  const p = w.player;
  const spread = def.spreadDeg * DEG;

  // Un événement par VOLÉE (pas seulement au déclenchement) : une « traînée de
  // salves » de douze volées doit s'entendre douze fois, pas une. `a` = index de
  // l'arme, `d` = sa forme — la présentation choisit son son/effet sans jamais
  // tester un identifiant textuel.
  w.events.push(Ev.SecondaryFired, p.secondary, p.x, p.y, def.shape);

  /* Dégâts EFFECTIFS : la définition donne le nominal, la carte et le vaisseau
     le multiplient. On lit le cache `progress.stats` (jamais un nouvel état par
     tick) et on multiplie ici, à l'émission, pour que chaque projectile parte
     avec la bonne valeur dans `pb.damage` — la collision n'a alors rien de
     particulier à savoir. À 1 (le défaut), le produit vaut exactement le
     nominal : aucune dérive flottante sur une run sans amélioration. */
  const damage = def.damage * w.progress.stats.secondaryDamageMul;

  switch (def.shape) {
    case SecShape.Lance: {
      /* Colonne de projectiles alignés sur la même verticale, tirés au même
         instant et à la même vitesse : ils conservent donc leur espacement tout
         le vol. Le premier meurt sur sa cible, le deuxième arrive
         `spacing / speed` seconde plus tard et poursuit — la perforation est
         émergente, sans un seul champ de plus dans le pool ni une seule branche
         de plus dans la boucle de collision. */
      for (let i = 0; i < def.count; i++) {
        emit(w, p.x, p.y + MUZZLE_Y + i * def.spacing, 0, def.speed, def.radius, damage);
      }
      break;
    }

    case SecShape.Flanc: {
      /* Deux salves horizontales symétriques. L'étalement est ANGULAIRE et non
         positionnel : trois projectiles partant du même point à ±13° couvrent
         un cône qui s'élargit avec la distance, là où trois lignes parallèles
         laisseraient passer un ennemi entre elles à toute distance. */
      for (let s = -1; s <= 1; s += 2) {
        for (let i = 0; i < def.count; i++) {
          const t = def.count === 1 ? 0 : i / (def.count - 1) - 0.5;
          const a = t * spread;
          emit(
            w,
            p.x + s * def.spacing,
            p.y,
            s * cos(a) * def.speed,
            sin(a) * def.speed,
            def.radius,
            damage,
          );
        }
      }
      break;
    }

    case SecShape.Faisceau: {
      // Un segment par volée, droit devant. La sensation de faisceau CONTINU
      // vient de la fréquence (`shotEvery` ≈ 30 ms) et du rayon, pas d'une
      // primitive de rendu : la simulation ne connaît que des projectiles, et
      // c'est ce qui garde la collision balayée valable pour cette arme aussi.
      for (let i = 0; i < def.count; i++) {
        const t = def.count === 1 ? 0 : i / (def.count - 1) - 0.5;
        emit(w, p.x + t * def.spacing, p.y + MUZZLE_Y, 0, def.speed, def.radius, damage);
      }
      break;
    }

    case SecShape.Mine: {
      /* Charges déposées sur une ligne horizontale, avec une dérive latérale
         proportionnelle à leur écart au centre : le champ s'ÉVASE en montant.
         Sans cette dérive, quatre mines parallèles laissent quatre couloirs
         libres permanents et le champ n'interdit rien.

         Leur vitesse verticale est faible mais jamais nulle : une mine
         strictement immobile ne sortirait jamais du terrain et ne serait donc
         jamais recyclée par `motionSystem` — c'est une fuite de pool, pas un
         effet de jeu. */
      for (let i = 0; i < def.count; i++) {
        const t = def.count === 1 ? 0 : i / (def.count - 1) - 0.5;
        emit(
          w,
          p.x + t * def.spacing,
          p.y + MUZZLE_Y,
          t * def.speed * 0.45,
          def.speed,
          def.radius,
          damage,
        );
      }
      break;
    }

    case SecShape.Traqueur: {
      /* Les missiles partent en ÉVENTAIL, et c'est le contraire d'un détail :
         lancés tous parallèles, ils convergeraient invisiblement vers la même
         cible et le joueur ne verrait qu'un tir groupé un peu lent. Écartés au
         départ, ils se REFERMENT sur la cible — la courbe se voit, donc le
         guidage se voit. Une mécanique qu'on ne peut pas percevoir n'existe pas,
         quel que soit le soin mis à l'écrire.

         L'éventail sert aussi la tenue de l'arme : trois missiles ouverts à 30°
         acquièrent trois cibles différentes dans une nuée, là où trois missiles
         alignés se consumeraient tous sur la première. */
      const rate = (def.turnDeg ?? 0) * DEG;
      for (let i = 0; i < def.count; i++) {
        const t = def.count === 1 ? 0 : i / (def.count - 1) - 0.5;
        const a = t * spread;
        const slot = emit(
          w,
          p.x + t * def.spacing,
          p.y + MUZZLE_Y,
          sin(a) * def.speed,
          cos(a) * def.speed,
          def.radius,
          damage,
        );
        if (slot >= 0) w.pb.behave(slot, PbMode.Homing, rate, 0, 1);
      }
      break;
    }

    case SecShape.Scie: {
      /* Disques lancés droit devant, écartés de `spacing`. Ils montent pendant
         `duration`, puis reviennent — le budget de contacts (`hits`) est commun
         aux deux trajets, donc un disque qui a tout dépensé à l'aller ne rapporte
         rien au retour. C'est ce qui empêche l'arme de valoir mécaniquement le
         double de ce qu'elle annonce. */
      const rate = (def.turnDeg ?? 0) * DEG;
      const hits = def.hits ?? 1;
      for (let i = 0; i < def.count; i++) {
        const t = def.count === 1 ? 0 : i / (def.count - 1) - 0.5;
        const a = t * spread;
        const slot = emit(
          w,
          p.x + t * def.spacing,
          p.y + MUZZLE_Y,
          sin(a) * def.speed,
          cos(a) * def.speed,
          def.radius,
          damage,
        );
        if (slot >= 0) w.pb.behave(slot, PbMode.Pierce, rate, def.duration ?? 0, hits);
      }
      break;
    }

    case SecShape.Arc: {
      /* Le trait part droit devant, et sa direction initiale n'a presque aucune
         importance : l'acquisition d'un arc ignore les angles. Ce qui compte est
         qu'il naisse à la bouche, donc au centre de la mêlée si le joueur s'y
         est mis — l'arc est une arme qu'on joue en s'approchant, ce qu'aucune
         autre du catalogue ne demande. */
      const rate = (def.turnDeg ?? 0) * DEG;
      const hits = def.hits ?? 1;
      for (let i = 0; i < def.count; i++) {
        const t = def.count === 1 ? 0 : i / (def.count - 1) - 0.5;
        const a = t * spread;
        const slot = emit(
          w,
          p.x + t * def.spacing,
          p.y + MUZZLE_Y,
          sin(a) * def.speed,
          cos(a) * def.speed,
          def.radius,
          damage,
        );
        if (slot >= 0) w.pb.behave(slot, PbMode.Arc, rate, 0, hits);
      }
      break;
    }

    case SecShape.Rayon: {
      /* Une colonne de segments ANCRÉS, tous nés au même instant et tous
         repositionnés sur le vaisseau à chaque tick par `steerSecondaries`.
         Leur vitesse est nulle : le rayon n'a pas de temps de vol, c'est sa
         raison d'être.

         `spacing` est le pas de la colonne, et il doit rester sous le diamètre
         d'un segment, sinon le rayon devient un pointillé — un trait discontinu
         laisse passer un ennemi entre deux points et le joueur ne peut pas
         comprendre pourquoi son rayon « ne touche pas ». */
      const life = def.duration ?? 0;
      for (let i = 0; i < def.count; i++) {
        const off = i * def.spacing;
        const slot = emit(w, p.x, p.y + MUZZLE_Y + off, 0, 0, def.radius, damage);
        // `param` porte ici la hauteur d'ancrage : c'est elle qui rend la
        // colonne reconstructible à chaque tick sans mémoriser d'ordre.
        if (slot >= 0) w.pb.behave(slot, PbMode.Beam, off, life, 0);
      }
      break;
    }

    default:
      break;
  }
}

/** La version ULTIME d'une secondaire : sa volée, mais massivement grossie —
 *  bien plus de projectiles, bien plus de dégâts, bien plus large. On réutilise
 *  ainsi la géométrie de CHAQUE arme sans code par arme : le laser devient un
 *  faisceau énorme, les missiles une nuée, la mine un champ, etc. */
function ultiScaled(def: SecondaryDef): SecondaryDef {
  return {
    ...def,
    count: Math.max(1, Math.round(def.count * ULTI_COUNT_MUL)),
    damage: def.damage * ULTI_DMG_MUL,
    radius: def.radius * ULTI_RADIUS_MUL,
    spreadDeg: def.spreadDeg > 0 ? def.spreadDeg * 1.4 : 40,
    spacing: def.spacing * 1.3,
  };
}

export function secondarySystem(w: World, input: InputFrame): void {
  const p = w.player;

  /* ⚠️ EN TÊTE, avant toute sortie anticipée. Les projectiles déjà en vol ne
     dépendent plus de l'arme équipée : changer d'arme, mourir, ou n'en avoir
     aucune ne doit pas figer un missile en plein virage. La sortie « aucune
     secondaire équipée » juste en dessous a exactement ce défaut si on place cet
     appel après elle — et le symptôme, des missiles qui filent tout droit après
     la mort du joueur, serait attribué à n'importe quoi sauf à un ordre
     d'appel. */
  steerSecondaries(w);

  // `noUncheckedIndexedAccess` rend cette lecture `SecondaryDef | undefined`, et
  // c'est utile ici : `secondary` vaut -1 quand aucune arme n'est équipée, et un
  // monde construit sur `EMPTY_INDEX` (tests unitaires, banc de charge) n'a
  // aucune secondaire. Les deux cas se traitent par la même garde.
  const def = w.defs.secondaries[p.secondary];
  if (def === undefined) {
    // On mémorise quand même l'état du bouton : sinon, équiper une arme pendant
    // que le clic est déjà enfoncé produirait un front montant fantôme au
    // premier tick suivant, et un tir que le joueur n'a pas demandé.
    p.secondaryHeld = input.secondary;
    return;
  }

  /* ── recharge ────────────────────────────────────────────────────────────
     Elle tourne inconditionnellement, mort comprise. Décision assumée : perdre
     une vie coûte déjà une vie, et sortir de réapparition avec une secondaire
     encore vide punirait deux fois le même instant. */
  if (p.secondaryCd > 0) {
    const cd = p.secondaryCd - DT;
    p.secondaryCd = cd > 0 ? cd : 0;
  }

  if (p.dead) {
    // Une rafale en cours s'interrompt : le vaisseau n'est plus sur le terrain,
    // et laisser le faisceau continuer depuis sa dernière position ferait tirer
    // une épave.
    p.secondaryShots = 0;
    p.secondaryShotT = 0;
    p.ultiShots = 0;
    p.ultiShotT = 0;
    p.secondaryHeld = input.secondary;
    return;
  }

  /* ── rafale en cours ─────────────────────────────────────────────────────
     Traitée AVANT le déclenchement, pour qu'une rafale démarrée à ce tick ne
     soit pas immédiatement re-servie ici et ne tire pas deux volées d'un coup. */
  if (p.secondaryShots > 0) {
    // Garde-fou : une période nulle ou négative — donnée de contenu erronée —
    // rendrait la boucle ci-dessous non bornée en temps. On la ramène au pas de
    // simulation, ce qui donne une volée par tick : dégradé, jamais bloqué.
    const every = def.shotEvery > 0 ? def.shotEvery : DT;
    let t = p.secondaryShotT - DT;
    let left = p.secondaryShots;
    // `while` et non `if` : à 30 ms de période et 8,3 ms de tick le cas ne se
    // présente pas, mais une arme réglée sous le pas de simulation étirerait
    // sinon sa rafale au lieu de la tirer — sa durée dépendrait de la cadence de
    // simulation, ce qui est exactement ce que le pas fixe existe pour éviter.
    while (t <= 0 && left > 0) {
      volley(w, def);
      left--;
      t += every;
    }
    p.secondaryShots = left;
    p.secondaryShotT = left > 0 ? t : 0;
  }

  /* ── décharge d'ULTI en cours ────────────────────────────────────────────
     La rafale MONSTRUEUSE : la volée de la secondaire courante, massivement
     scalée (count/dégâts/rayon). Le télégraphe (charge) et le déclenchement
     vivent dans `playerSystem` ; ici on ne fait que TIRER. Indépendante de la
     rafale/recharge normale — l'ulti ne consomme pas la secondaire. */
  if (p.ultiShots > 0) {
    const ud = ultiScaled(def);
    let t = p.ultiShotT - DT;
    let left = p.ultiShots;
    while (t <= 0 && left > 0) {
      volley(w, ud);
      left--;
      t += ULTI_SHOT_EVERY;
    }
    p.ultiShots = left;
    p.ultiShotT = left > 0 ? t : 0;
  }

  /* ── déclenchement au FRONT MONTANT ──────────────────────────────────────
     Même raisonnement que la bombe : maintenir le clic droit ne doit rien
     déclencher de plus qu'un appui. Sans ça, un joueur qui garde le bouton
     enfoncé — ce qui est le réflexe naturel quand on attend une recharge —
     redéclencherait l'arme au tick exact où elle redevient prête, sans l'avoir
     décidé. La secondaire est le SEUL moment de décision du jeu ; la laisser
     partir toute seule la supprimerait. */
  const pressed = input.secondary && !p.secondaryHeld;
  p.secondaryHeld = input.secondary;

  if (!pressed) return;
  if (p.secondaryCd > 0 || p.secondaryShots > 0) return;

  /* Recharge EFFECTIVE : le nominal de la définition, accéléré par le vaisseau
     (FANAL ×0,5) et les cartes de recharge, via le cache `progress.stats`. On
     ne borne PAS ici à `SECONDARY_CD_MIN` : le FANAL sort volontairement la
     secondaire de la borne basse [2, 8] s (voir son fichier), et c'est le
     plancher du multiplicateur (`STAT_FLOORS`, ×0,4) qui empêche la dérive vers
     une primaire bis. À 1 (le défaut), le produit vaut exactement le nominal. */
  p.secondaryCd = def.cooldown * w.progress.stats.secondaryCooldownMul;

  // Première volée tirée SUR-LE-CHAMP, avant même d'armer le compte à rebours
  // des suivantes. Un délai d'un seul tick entre le clic et le projectile se
  // ressent comme de la latence sur une arme qu'on déclenche exprès — c'est
  // précisément la différence entre une arme qui claque et une arme molle.
  volley(w, def);
  const left = def.shots - 1;
  p.secondaryShots = left > 0 ? left : 0;
  p.secondaryShotT = left > 0 ? (def.shotEvery > 0 ? def.shotEvery : DT) : 0;
  // (L'événement SecondaryFired est désormais émis PAR VOLÉE, dans `volley()`.)
}
